import { LegalPage, Section, LegalFooter } from "./legal.privacy";

export function meta() {
  return [{ title: "Terms & Conditions — Orchestrate" }];
}

export default function TermsAndConditions() {
  return (
    <LegalPage title="Terms & Conditions">
      <Section title="1. Agreement to Terms">
        <p>
          These Terms and Conditions (&quot;Terms&quot;) govern your access to and use of the
          Orchestrate platform (&quot;Service&quot;) operated by APTIM-Solutions Ltd
          (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;), a company
          registered in England and Wales.
        </p>
        <p>
          By accessing or using the Service, you agree to be bound by these Terms. If you do not
          agree to these Terms, you must not access or use the Service. If you are using the Service
          on behalf of an organisation, you represent that you have the authority to bind that
          organisation to these Terms.
        </p>
      </Section>

      <Section title="2. Description of Service">
        <p>
          Orchestrate is a programme and project governance platform that provides structured
          delivery management, stage-gate quality scoring, RAID lifecycle management, resource
          oversight, budget tracking, and related project governance capabilities. The Service is
          provided as a software-as-a-service (SaaS) application.
        </p>
        <p>
          The Service includes optional AI-powered features (such as intelligent suggestions and
          project assistance) that use third-party artificial intelligence services. The Service also
          uses third-party analytics and error monitoring tools to maintain quality and improve the
          user experience. Details of these third-party services are set out in Sections 5A and 5B
          below.
        </p>
      </Section>

      <Section title="3. Account Registration and Security">
        <p>
          To use the Service, you must register for an account or be invited by an existing user.
          You agree to:
        </p>
        <ul>
          <li>Provide accurate and complete registration information</li>
          <li>Maintain the security of your account credentials</li>
          <li>Promptly notify us of any unauthorised access to your account</li>
          <li>Accept responsibility for all activities that occur under your account</li>
        </ul>
        <p>
          Authentication is managed through Auth0. You are responsible for complying with
          Auth0&apos;s terms of service in relation to your authentication credentials.
        </p>
      </Section>

      <Section title="4. Acceptable Use">
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose or in violation of any applicable law</li>
          <li>Attempt to gain unauthorised access to any part of the Service or its systems</li>
          <li>Interfere with or disrupt the integrity or performance of the Service</li>
          <li>Upload or transmit any malicious code, viruses, or harmful content</li>
          <li>
            Share your account credentials with third parties or allow multiple individuals to use a
            single account
          </li>
          <li>
            Scrape, crawl, or use automated means to access the Service without our prior written
            consent
          </li>
          <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
        </ul>
      </Section>

      <Section title="5. Data Ownership and Intellectual Property">
        <p>
          <strong>Your data:</strong> You retain all rights to the data you input into the Service
          (&quot;Customer Data&quot;). We do not claim ownership of your Customer Data. You grant us
          a limited licence to process your Customer Data solely for the purpose of providing and
          improving the Service.
        </p>
        <p>
          <strong>Our intellectual property:</strong> The Service, including its software, design,
          features, documentation, and all related intellectual property, is owned by
          APTIM-Solutions Ltd and is protected by copyright, trademark, and other intellectual
          property laws. Nothing in these Terms grants you any right to use our trademarks, logos,
          or branding without our prior written consent.
        </p>
      </Section>

      <Section title="5A. Artificial Intelligence Features">
        <p>
          The Service includes optional AI-powered features that use Anthropic&apos;s Claude API to
          provide intelligent suggestions, project assistance, and content generation. By using these
          features, you acknowledge and agree that:
        </p>
        <ul>
          <li>
            Relevant project data (such as project descriptions, RAID items, and governance
            information) may be sent to Anthropic&apos;s servers for processing in order to generate
            AI responses.
          </li>
          <li>
            AI-generated outputs are provided as suggestions only and should not be relied upon as a
            substitute for professional judgement. You are responsible for reviewing and validating
            any AI-generated content before acting on it.
          </li>
          <li>
            We do not use your data to train AI models. Anthropic&apos;s API usage is governed by
            their{" "}
            <a
              href="https://www.anthropic.com/policies/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-brand hover:underline"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://www.anthropic.com/policies/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-brand hover:underline"
            >
              Privacy Policy
            </a>
            , which confirm that API inputs are not used for model training.
          </li>
          <li>
            AI features may be disabled by your organisation administrator. Where AI features are
            not enabled, no data is sent to Anthropic.
          </li>
        </ul>
      </Section>

      <Section title="5B. Third-Party Services">
        <p>
          The Service integrates with the following third-party services. By using the Service, you
          acknowledge that your data may be processed by these providers in accordance with their
          respective terms and privacy policies:
        </p>
        <ul>
          <li>
            <strong>Auth0 (Okta):</strong> Identity and authentication management. Handles login,
            session management, and credential storage.
          </li>
          <li>
            <strong>Anthropic (Claude API):</strong> Artificial intelligence features including
            project assistance and intelligent suggestions. See Section 5A above.
          </li>
          <li>
            <strong>Microsoft Clarity:</strong> Anonymised session analytics including heatmaps and
            session recordings to help us understand how users interact with the platform. Clarity
            is only loaded if you consent to analytics cookies.
          </li>
          <li>
            <strong>Sentry:</strong> Error monitoring and performance tracking to help us identify
            and resolve technical issues quickly. Sentry may collect technical data such as browser
            type, operating system, error stack traces, and session identifiers.
          </li>
          <li>
            <strong>Resend:</strong> Transactional email delivery for invitations and notifications.
          </li>
        </ul>
        <p>
          We select third-party providers that maintain appropriate security and data protection
          standards. Each provider processes data only as necessary to deliver their specific
          function and is bound by their own terms of service and privacy commitments.
        </p>
      </Section>

      <Section title="6. Multi-Tenancy and Data Isolation">
        <p>
          The Service operates a multi-tenant architecture where each organisation&apos;s data is
          logically isolated. We implement strict access controls to ensure that users can only
          access data belonging to their own organisation and projects to which they have been
          granted access. Organisation administrators are responsible for managing user access and
          roles within their organisation.
        </p>
      </Section>

      <Section title="7. Service Availability">
        <p>
          We aim to provide the Service with high availability but do not guarantee uninterrupted
          access. We may temporarily suspend the Service for maintenance, updates, or security
          purposes. Where planned maintenance is required, we will endeavour to provide at least 5
          days&apos; advance notice.
        </p>
        <p>
          We are not liable for any downtime, data loss, or service interruptions caused by factors
          outside our reasonable control, including but not limited to internet outages, third-party
          service failures, and force majeure events.
        </p>
      </Section>

      <Section title="8. Fees and Payment">
        <p>
          Access to the Service may be provided under an APTIM-Solutions engagement agreement, a
          standalone subscription, or a trial arrangement. Specific pricing, billing terms, and
          payment obligations will be set out in a separate agreement or order form. Free
          stakeholder access is provided at no charge for read-only users.
        </p>
      </Section>

      <Section title="9. Termination">
        <p>
          Either party may terminate the use of the Service by providing written notice. Upon
          termination:
        </p>
        <ul>
          <li>Your access to the Service will be revoked</li>
          <li>You may request an export of your Customer Data within 30 days of termination</li>
          <li>
            After the 30-day period, we may delete your Customer Data in accordance with our data
            retention policy
          </li>
        </ul>
        <p>
          We reserve the right to suspend or terminate your access immediately if you breach these
          Terms, engage in activities that threaten the security of the Service, or fail to pay
          applicable fees.
        </p>
      </Section>

      <Section title="10. Limitation of Liability">
        <p>To the fullest extent permitted by applicable law:</p>
        <ul>
          <li>
            The Service is provided &quot;as is&quot; and &quot;as available&quot; without
            warranties of any kind, whether express or implied.
          </li>
          <li>
            We shall not be liable for any indirect, incidental, special, consequential, or punitive
            damages, including but not limited to loss of profits, data, business opportunities, or
            goodwill.
          </li>
          <li>
            Our total aggregate liability arising out of or in connection with these Terms shall not
            exceed the total fees paid by you in the twelve months preceding the claim.
          </li>
        </ul>
        <p>
          Nothing in these Terms excludes or limits liability for death or personal injury caused by
          negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be
          excluded or limited by English law.
        </p>
      </Section>

      <Section title="11. Indemnification">
        <p>
          You agree to indemnify and hold harmless APTIM-Solutions Ltd, its directors, employees,
          and agents from any claims, damages, losses, or expenses (including reasonable legal fees)
          arising from your use of the Service, your violation of these Terms, or your violation of
          any third-party rights.
        </p>
      </Section>

      <Section title="12. Governing Law and Jurisdiction">
        <p>
          These Terms shall be governed by and construed in accordance with the laws of England and
          Wales. Any disputes arising under or in connection with these Terms shall be subject to
          the exclusive jurisdiction of the courts of England and Wales.
        </p>
      </Section>

      <Section title="13. Changes to Terms">
        <p>
          We reserve the right to modify these Terms at any time. We will notify you of material
          changes by posting the updated Terms on this page and, where appropriate, by email
          notification. Your continued use of the Service after such changes constitutes acceptance
          of the modified Terms.
        </p>
      </Section>

      <Section title="14. Contact">
        <p>For questions about these Terms, please contact us:</p>
        <ul>
          <li>
            Email:{" "}
            <a
              href="mailto:legal@aptim-solutions.com"
              className="text-accent-brand hover:underline"
            >
              legal@aptim-solutions.com
            </a>
          </li>
          <li>APTIM-Solutions Ltd</li>
          <li>Registered in England and Wales</li>
        </ul>
      </Section>
    </LegalPage>
  );
}
